import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Users, Search, Loader2, Copy, Check, MoreVertical,
  UserCog, Mail, RefreshCw, Sparkles, Star, StarOff, Shield, X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface Member {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  announcement_credits: number | null;
  ai_assist_uses_remaining: number | null;
  has_purchased_bundle: boolean | null;
  highest_bundle_purchased: string | null;
  referral_code: string | null;
  influencer_access: boolean | null;
  influencer_access_expires_at: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  announcement_count: number;
  affirmation_generation_count: number;
  is_banned: boolean;
}

const PLANS = ['free', 'starter', 'connection', 'legacy'] as const;
type Plan = typeof PLANS[number];

const SITE_ORIGIN = 'https://say-it-now.app';

function planLabel(m: Member): string {
  if (m.has_purchased_bundle && m.highest_bundle_purchased) {
    return m.highest_bundle_purchased.charAt(0).toUpperCase() + m.highest_bundle_purchased.slice(1);
  }
  return 'Free';
}

function planVariant(m: Member): 'default' | 'secondary' | 'outline' {
  const p = m.highest_bundle_purchased;
  if (p === 'legacy') return 'default';
  if (p === 'connection') return 'secondary';
  return 'outline';
}

export default function AdminAllMembers() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<{ member: Member; plan: Plan; credits: string; note: string } | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ title: string; description: string; onConfirm: () => Promise<unknown> | unknown } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase.rpc('has_role', { _user_id: user.id, _role: 'admin' }).then(({ data }) => {
      setIsAdmin(!!data);
    });
  }, [user?.id]);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_list_members' as never, {
      p_search: q, p_limit: 500, p_offset: 0,
    } as never);
    if (error) {
      toast.error('Failed to load members');
      console.error(error);
    } else {
      setMembers((data as unknown as Member[]) || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (isAdmin) load(''); }, [isAdmin, load]);

  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
      toast.success('Copied');
    } catch { toast.error('Copy failed'); }
  };

  const updateMember = async (
    userId: string,
    args: { plan?: Plan | null; credits?: number | null; credit_delta?: number | null; make_influencer?: boolean | null; note?: string | null },
  ) => {
    const { error } = await supabase.rpc('admin_set_member_plan' as never, {
      p_target_user_id: userId,
      p_plan: args.plan ?? null,
      p_credits: args.credits ?? null,
      p_credit_delta: args.credit_delta ?? null,
      p_make_influencer: args.make_influencer ?? null,
      p_note: args.note ?? null,
    } as never);
    if (error) { toast.error(error.message); return false; }
    toast.success('Member updated');
    await load(search);
    return true;
  };

  const resetAffirmations = async (userId: string) => {
    const { error } = await supabase.rpc('admin_reset_affirmation_usage' as never, { p_target_user_id: userId } as never);
    if (error) { toast.error(error.message); return; }
    toast.success('Affirmation quota reset');
    load(search);
  };

  const resendMagic = async (email: string) => {
    const { data, error } = await supabase.functions.invoke('admin-actions', {
      body: { action: 'resend_magic_link', email },
    });
    if (error || data?.error) { toast.error(error?.message || data?.error || 'Failed'); return; }
    if (data?.email_sent) toast.success(`Magic link sent to ${email}`);
    else toast.warning(`Magic link generated. ${data?.email_error || 'Copy it manually.'}`);
    if (data?.magic_link) copy(data.magic_link, `magic-${email}`);
  };

  if (isAdmin === null) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!isAdmin) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><p className="text-muted-foreground">Admin access required.</p></div>;
  }

  const filtered = members; // server-side search

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center gap-4 px-4">
          <Link to="/admin" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Admin Hub</span>
          </Link>
          <h1 className="text-base sm:text-lg font-bold text-foreground">All Members</h1>
        </div>
      </header>

      <main className="container py-6 max-w-6xl px-4 space-y-6">
        {/* Upgrade Existing Member */}
        <UpgradeExistingMemberCard members={members} onUpdated={() => load(search)} />

        {/* Members list */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Users className="h-5 w-5 text-primary" /> All Members
                </CardTitle>
                <CardDescription>
                  {filtered.length} registered member{filtered.length !== 1 ? 's' : ''} — free, paid, influencers, and admins.
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Input
                placeholder="Search by email or name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load(search)}
              />
              <Button onClick={() => load(search)} disabled={loading} size="icon" aria-label="Search">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            {loading && members.length === 0 ? (
              <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No members found.</p>
            ) : (
              <div className="space-y-2">
                {filtered.map((m) => (
                  <MemberCard
                    key={m.user_id}
                    m={m}
                    onEdit={() => setEditing({ member: m, plan: (m.highest_bundle_purchased as Plan) || 'free', credits: String(m.announcement_credits ?? 0), note: '' })}
                    onAddCredits={() => setConfirmAction({
                      title: 'Add 1 credit?',
                      description: `Give ${m.email} one extra announcement credit.`,
                      onConfirm: () => updateMember(m.user_id, { credit_delta: 1, note: 'Quick +1 credit' }),
                    })}
                    onRemoveCredits={() => setConfirmAction({
                      title: 'Remove 1 credit?',
                      description: `Take 1 credit away from ${m.email}.`,
                      onConfirm: () => updateMember(m.user_id, { credit_delta: -1, note: 'Quick -1 credit' }),
                    })}
                    onResetAffirm={() => setConfirmAction({
                      title: 'Reset affirmation quota?',
                      description: `Reset ${m.email}'s monthly affirmation image usage to 0.`,
                      onConfirm: () => resetAffirmations(m.user_id),
                    })}
                    onToggleInfluencer={() => setConfirmAction({
                      title: m.influencer_access ? 'Remove influencer status?' : 'Mark as influencer?',
                      description: `${m.influencer_access ? 'Revoke' : 'Grant'} influencer access for ${m.email}.`,
                      onConfirm: () => updateMember(m.user_id, { make_influencer: !m.influencer_access, note: 'Influencer flag toggled' }),
                    })}
                    onCopyRef={() => m.referral_code && copy(`${SITE_ORIGIN}/?ref=${m.referral_code}`, `ref-${m.user_id}`)}
                    onResendMagic={() => setConfirmAction({
                      title: 'Resend magic link?',
                      description: `Email a fresh sign-in link to ${m.email}.`,
                      onConfirm: () => resendMagic(m.email),
                    })}
                    copiedKey={copied}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit dialog */}
      {editing && (
        <AlertDialog open onOpenChange={(o) => !o && setEditing(null)}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>Change plan & credits</AlertDialogTitle>
              <AlertDialogDescription className="break-words">
                You are about to change <strong>{editing.member.email}</strong>'s plan. Continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Plan</label>
                <Select value={editing.plan} onValueChange={(v) => setEditing((e) => e && { ...e, plan: v as Plan })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLANS.map((p) => (
                      <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Credits (absolute)</label>
                <Input type="number" min="0" value={editing.credits} onChange={(e) => setEditing((s) => s && { ...s, credits: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Note / reason (optional)</label>
                <Textarea rows={2} value={editing.note} onChange={(e) => setEditing((s) => s && { ...s, note: e.target.value })} />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  if (!editing) return;
                  const ok = await updateMember(editing.member.user_id, {
                    plan: editing.plan,
                    credits: parseInt(editing.credits) || 0,
                    note: editing.note || null,
                  });
                  if (ok) setEditing(null);
                }}
              >Confirm change</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Generic confirm dialog */}
      {confirmAction && (
        <AlertDialog open onOpenChange={(o) => !o && setConfirmAction(null)}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmAction.title}</AlertDialogTitle>
              <AlertDialogDescription className="break-words">{confirmAction.description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={async () => { await confirmAction.onConfirm(); setConfirmAction(null); }}>
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

/* ─── Member row card (mobile-first) ─── */
function MemberCard({
  m, onEdit, onAddCredits, onRemoveCredits, onResetAffirm, onToggleInfluencer, onCopyRef, onResendMagic, copiedKey,
}: {
  m: Member;
  onEdit: () => void;
  onAddCredits: () => void;
  onRemoveCredits: () => void;
  onResetAffirm: () => void;
  onToggleInfluencer: () => void;
  onCopyRef: () => void;
  onResendMagic: () => void;
  copiedKey: string | null;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3 sm:p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <p className="text-sm font-semibold text-foreground truncate">{m.full_name || m.email.split('@')[0]}</p>
            {m.role === 'admin' && <Badge variant="default" className="text-[10px] bg-amber-600 hover:bg-amber-600"><Shield className="h-3 w-3 mr-0.5" />Admin</Badge>}
            {m.influencer_access && <Badge variant="secondary" className="text-[10px]"><Star className="h-3 w-3 mr-0.5" />Influencer</Badge>}
            {m.is_banned && <Badge variant="destructive" className="text-[10px]">Banned</Badge>}
          </div>
          <p className="text-xs text-muted-foreground truncate break-all">{m.email}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0"><MoreVertical className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Member actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={onEdit}><UserCog className="h-4 w-4 mr-2" />Change plan / credits</DropdownMenuItem>
            <DropdownMenuItem onClick={onAddCredits}>+1 credit</DropdownMenuItem>
            <DropdownMenuItem onClick={onRemoveCredits}>−1 credit</DropdownMenuItem>
            <DropdownMenuItem onClick={onResetAffirm}><Sparkles className="h-4 w-4 mr-2" />Reset affirmation quota</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onToggleInfluencer}>
              {m.influencer_access ? <><StarOff className="h-4 w-4 mr-2" />Remove influencer</> : <><Star className="h-4 w-4 mr-2" />Mark as influencer</>}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onCopyRef} disabled={!m.referral_code}>
              {copiedKey === `ref-${m.user_id}` ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              Copy referral link
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onResendMagic}><Mail className="h-4 w-4 mr-2" />Resend magic link</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <Stat label="Plan"><Badge variant={planVariant(m)} className="text-[10px]">{planLabel(m)}</Badge></Stat>
        <Stat label="Credits">{m.announcement_credits ?? 0}</Stat>
        <Stat label="Announcements">{m.announcement_count}</Stat>
        <Stat label="Affirmations">{m.affirmation_generation_count}</Stat>
        <Stat label="Joined">{m.created_at ? format(new Date(m.created_at), 'MMM d, yyyy') : '—'}</Stat>
        <Stat label="Last active">{m.last_sign_in_at ? format(new Date(m.last_sign_in_at), 'MMM d, yyyy') : '—'}</Stat>
        <Stat label="Ref code"><span className="font-mono">{m.referral_code || '—'}</span></Stat>
        <Stat label="Role"><span className="capitalize">{m.role}</span></Stat>
      </div>
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-foreground truncate">{children}</div>
    </div>
  );
}

/* ─── Upgrade Existing Member card ─── */
function UpgradeExistingMemberCard({ members, onUpdated }: { members: Member[]; onUpdated: () => void }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Member | null>(null);
  const [plan, setPlan] = useState<Plan>('connection');
  const [credits, setCredits] = useState('');
  const [makeInfluencer, setMakeInfluencer] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const q = query.trim().toLowerCase();
  const suggestions = q.length >= 2
    ? members.filter((m) => m.email.toLowerCase().includes(q) || (m.full_name || '').toLowerCase().includes(q)).slice(0, 6)
    : [];

  const submit = async () => {
    if (!selected) { toast.error('Select a member first'); return; }
    setSaving(true);
    const { error } = await supabase.rpc('admin_set_member_plan' as never, {
      p_target_user_id: selected.user_id,
      p_plan: plan,
      p_credits: credits ? parseInt(credits) : null,
      p_credit_delta: null,
      p_make_influencer: makeInfluencer ? true : null,
      p_note: note || 'Upgrade Existing Member',
    } as never);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Member access updated successfully.');
    setSelected(null); setQuery(''); setCredits(''); setNote(''); setMakeInfluencer(false);
    onUpdated();
  };

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <UserCog className="h-5 w-5 text-primary" />
          Upgrade Existing Member
        </CardTitle>
        <CardDescription>
          Update an existing user's plan or credits. Does not create a new account or send a new magic link.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Search by email or name *</label>
          <div className="relative">
            <Input
              value={selected ? `${selected.email}` : query}
              onChange={(e) => { setSelected(null); setQuery(e.target.value); }}
              placeholder="member@example.com"
            />
            {selected && (
              <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => { setSelected(null); setQuery(''); }}><X className="h-3 w-3" /></Button>
            )}
            {!selected && suggestions.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-md">
                {suggestions.map((s) => (
                  <button
                    key={s.user_id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex justify-between gap-2"
                    onClick={() => { setSelected(s); setQuery(s.email); setPlan((s.highest_bundle_purchased as Plan) || 'free'); }}
                  >
                    <span className="truncate">{s.email}</span>
                    <span className="text-muted-foreground text-xs shrink-0">{planLabel(s)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">New plan</label>
            <Select value={plan} onValueChange={(v) => setPlan(v as Plan)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLANS.map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Credits override (optional)</label>
            <Input type="number" min="0" placeholder="leave blank to keep" value={credits} onChange={(e) => setCredits(e.target.value)} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input id="make-inf" type="checkbox" className="h-4 w-4" checked={makeInfluencer} onChange={(e) => setMakeInfluencer(e.target.checked)} />
          <label htmlFor="make-inf" className="text-sm text-foreground">Also grant influencer status</label>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Note / reason (optional)</label>
          <Textarea rows={2} placeholder="e.g. Comped Legacy plan — community contributor" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        <Button className="w-full" onClick={() => setConfirmOpen(true)} disabled={!selected || saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCog className="h-4 w-4 mr-2" />}
          Update Member Access
        </Button>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm member upgrade</AlertDialogTitle>
              <AlertDialogDescription className="break-words">
                You are about to change <strong>{selected?.email}</strong>'s plan to <strong>{plan}</strong>
                {credits && ` and set credits to ${credits}`}
                {makeInfluencer && `, and grant influencer status`}. Continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={async () => { setConfirmOpen(false); await submit(); }}>Continue</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
